import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { PhonogramCreateStore } from "../../store";
import { runInAction } from "mobx";
import { InputString } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { InterpreterCreate } from "../../../interpreter/components/create/InterpreterCreate";
import { InterpreterCreateStore } from "../../../interpreter/store";
import { InputNumber } from "../../../common/controls";
import { PhonogramIsrcCustomField } from "../custom/PhonogramIsrcCustomField";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { MusicalWorkLinkCreate } from "../../../musicalWorkLink/components/create/MusicalWorkLinkCreate";
import { MusicalWorkLinkCreateStore } from "../../../musicalWorkLink/store";
import { GridColumn } from "../../../common/types";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type PhonogramCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  trackNameSelectedValue?: { displayId: string; id: string } | null;
  onTrackNameChange?: (value: string) => void;
  trackName: string;
  trackNameError?: string;
  performersSelectedValues: { displayId: string; id: string }[];
  onPerformersSelected?: (value: { displayId: string; id: string }[]) => void;
  onPerformersChange?: (value: string) => void;
  performersOptions?: { displayId: string; id: string }[];
  performersError?: string;
  albumNameSelectedValue?: { displayId: string; id: string } | null;
  onAlbumNameChange?: (value: string) => void;
  albumName: string;
  albumNameError?: string;
  publicationYearSelectedValue?: { displayId: string; id: string } | null;
  onPublicationYearChange?: (value: number | undefined) => void;
  publicationYear: number | undefined;
  publicationYearError?: string;
  isrcSelectedValue?: { displayId: string; id: string } | null;
  onIsrcChange?: (value: string) => void;
  isrc: string;
  isrcError?: string;
  originalProducerSelectedValue?: { displayId: string; id: string } | null;
  onOriginalProducerChange?: (value: string) => void;
  originalProducer: string;
  originalProducerError?: string;
  audioFileOnRestoreFile: (id: string) => void;
  audioFileOnLoadFile: (file: any) => void;
  audioFileOnCommitFile: (changes: GridChangeSet) => void;
  audioFileFiles: FileCRUDType[];
  audioFileError?: string;
  mainAudioSelectedValue?: { displayId: string; id: string } | null;
  onMainAudioChange?: (value: boolean) => void;
  mainAudio: boolean;
  mainAudioError?: string;
  linksOnRestore: (id: string) => void;
  linksOnCommitChanges: (changes: GridChangeSet) => void;
  linksValidateChanges: (changes: GridChangeSet) => boolean;
  linksRows: CrudRow[];
  musicalWorkLinkCreateStore: MusicalWorkLinkCreateStore;
  linksError?: string;
  linksVisibleColumns: GridColumn[];
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notes: string;
  notesError?: string;
  dummyCRUDRestoredObservable: number;
  phonogramCreateStore: PhonogramCreateStore;
};

export const PhonogramCreateForm: React.FC<PhonogramCreateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    trackName = "",
    trackNameError,
    onTrackNameChange = () => {},
    onPerformersSelected = () => {},
    performersSelectedValues,
    performersOptions = [],
    performersError,
    onPerformersChange = () => {},
    albumName = "",
    albumNameError,
    onAlbumNameChange = () => {},
    publicationYear = undefined,
    publicationYearError,
    onPublicationYearChange = () => {},
    isrc = "",
    isrcError,
    onIsrcChange = () => {},
    originalProducer = "",
    originalProducerError,
    onOriginalProducerChange = () => {},
    audioFileOnRestoreFile,
    audioFileOnLoadFile,
    audioFileOnCommitFile,
    audioFileFiles,
    audioFileError,
    mainAudio = false,
    mainAudioError,
    onMainAudioChange = () => {},
    linksOnRestore,
    linksOnCommitChanges,
    linksValidateChanges,
    linksRows,
    musicalWorkLinkCreateStore,
    linksError,
    linksVisibleColumns,
    notes = "",
    notesError,
    onNotesChange = () => {},
    dummyCRUDRestoredObservable,
    phonogramCreateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (musicalWorkLinkCreateStore.status === "uninitialized") {
      musicalWorkLinkCreateStore.initialize();
    }

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelNewEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormRecordProvider
            values={{
              trackName,
              albumName,
              publicationYear,
              isrc,
              originalProducer,
              mainAudio,
              notes,
            }}
          >
            <FormExtras
              entity="Phonogram"
              mode="create"
              values={{
                trackName,
                albumName,
                publicationYear,
                isrc,
                originalProducer,
                mainAudio,
                notes,
              }}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "trackName":
                    onTrackNameChange?.(valor);
                    break;
                  case "albumName":
                    onAlbumNameChange?.(valor);
                    break;
                  case "publicationYear":
                    onPublicationYearChange?.(valor);
                    break;
                  case "isrc":
                    onIsrcChange?.(valor);
                    break;
                  case "originalProducer":
                    onOriginalProducerChange?.(valor);
                    break;
                  case "mainAudio":
                    onMainAudioChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />

            <GroupContainer
              id="phonogramInfo"
              errorText={phonogramCreateStore.getCardErrors("phonogramInfo")}
              title={langStore.groupPhonogramPhonogramInfoLabel}
              collapsible={false}
              expanded={phonogramCreateStore.getCardState("phonogramInfo")}
              toggleExpand={() =>
                phonogramCreateStore.toggleCardState("phonogramInfo")
              }
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={trackName}
                  error={Boolean(trackNameError)}
                  warning={
                    !trackNameError &&
                    Boolean(phonogramCreateStore.warnings["trackName"])
                  }
                  helperText={
                    trackNameError ||
                    phonogramCreateStore.warnings["trackName"]?.message
                  }
                  label={langStore.PhonogramTrackNameLabel}
                  onChange={(value: string) => onTrackNameChange(value)}
                  description={langStore.PhonogramTrackNameDescription}
                />
                <InputReferenceSet
                  id="performers"
                  label={langStore.PhonogramPerformersLabel}
                  error={Boolean(performersError)}
                  helperText={
                    performersError ||
                    phonogramCreateStore.warnings["performers"]?.message
                  }
                  selectedValues={performersSelectedValues}
                  onChange={(value: string) => onPerformersChange(value)}
                  onSelect={(value: { displayId: string; id: string }[]) =>
                    onPerformersSelected(value)
                  }
                  options={performersOptions}
                  description={langStore.PhonogramPerformersDescription}
                  nullText={langStore.optionalFieldHint}
                  canCreate={rootStore.authStore.hasPermissions(
                    "interpreter_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new InterpreterCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <InterpreterCreate
                          interpreterCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewInterpreter"
                      cancelMethod="cancelNewInterpreter"
                    />
                  )}
                />

                <InputString
                  value={albumName}
                  error={Boolean(albumNameError)}
                  warning={
                    !albumNameError &&
                    Boolean(phonogramCreateStore.warnings["albumName"])
                  }
                  helperText={
                    albumNameError ||
                    phonogramCreateStore.warnings["albumName"]?.message
                  }
                  label={langStore.PhonogramAlbumNameLabel}
                  onChange={(value: string) => onAlbumNameChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramAlbumNameDescription}
                />
                <InputNumber
                  value={publicationYear}
                  nullable={true}
                  error={Boolean(publicationYearError)}
                  warning={
                    !publicationYearError &&
                    Boolean(phonogramCreateStore.warnings["publicationYear"])
                  }
                  helperText={
                    publicationYearError ||
                    phonogramCreateStore.warnings["publicationYear"]?.message
                  }
                  allowNegative={false}
                  label={langStore.PhonogramPublicationYearLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramPublicationYearDescription}
                  onChange={(value: number | undefined) =>
                    onPublicationYearChange(value)
                  }
                />
                <PhonogramIsrcCustomField
                  value={isrc}
                  error={Boolean(isrcError)}
                  helperText={
                    isrcError || phonogramCreateStore.warnings["isrc"]?.message
                  }
                  {...{ optional: true }}
                  label={langStore.PhonogramIsrcLabel}
                  description={langStore.PhonogramIsrcDescription}
                  onChange={(value: string) => onIsrcChange(value)}
                />

                <InputString
                  value={originalProducer}
                  error={Boolean(originalProducerError)}
                  warning={
                    !originalProducerError &&
                    Boolean(phonogramCreateStore.warnings["originalProducer"])
                  }
                  helperText={
                    originalProducerError ||
                    phonogramCreateStore.warnings["originalProducer"]?.message
                  }
                  label={langStore.PhonogramOriginalProducerLabel}
                  onChange={(value: string) => onOriginalProducerChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramOriginalProducerDescription}
                />
                <LoadFilesCRUDCompact
                  rows={audioFileFiles}
                  maxRows={1}
                  acceptFileTypes=".mp3,.m4a"
                  strictAccept={true}
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.PhonogramAudioFileLabel}
                  description={langStore.PhonogramAudioFileDescription}
                  showLabel={true}
                  onLoadFile={(file: File) => audioFileOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => audioFileOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    audioFileOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={audioFileError}
                />
                <InputBoolean
                  value={mainAudio}
                  error={Boolean(mainAudioError)}
                  helperText={
                    mainAudioError ||
                    phonogramCreateStore.warnings["mainAudio"]?.message
                  }
                  label={langStore.PhonogramMainAudioLabel}
                  description={langStore.PhonogramMainAudioDescription}
                  onChange={(value: boolean) => onMainAudioChange(value)}
                />
                <ItemsCRUDCompact
                  label={langStore.PhonogramLinksLabel}
                  description={langStore.PhonogramLinksDescription}
                  showLabel={true}
                  rows={linksRows}
                  columns={linksVisibleColumns}
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() => phonogramCreateStore.linksLoadRow(row));
                    }
                    return (
                      <MusicalWorkLinkCreate
                        musicalWorkLinkCreateStore={musicalWorkLinkCreateStore}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="sm"
                  onRestore={(id: string) => linksOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    linksOnCommitChanges(changes)
                  }
                  validate={(changes: GridChangeSet) =>
                    linksValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={linksError}
                />

                <InputText
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(phonogramCreateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    phonogramCreateStore.warnings["notes"]?.message
                  }
                  label={langStore.PhonogramNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PhonogramNotesDescription}
                />
              </FormControl>
            </GroupContainer>
          </FormRecordProvider>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
